import { IRynReplayIngestTokenSource } from '@ups/xplat/base/ryn-replay';
import { RynReplayBearerProvider } from './ryn-replay-bearer-provider';

/**
 * Client for `POST {ingest base}/ingest-token` (#30115 PR 2).
 *
 * WIRE CONTRACT, mirrored from `IngestTokenFunction`'s header comment:
 *   POST {ingest base}/ingest-token
 *   Authorization: Bearer <the app's own Auth0 or Entra access token>
 *   Origin: <browser-set, never by this code>
 *   body: {"sessionId":"<replay session id>"}
 *   200 {token, expiresAt, app}; 503 unkeyed; 401 bearer; 403 origin/client/subject
 *
 * The token is bound to ONE session id and ONE Origin and lives 10 minutes.
 * Every failure here resolves `null`, which the transport reads as "send the
 * shared key", so a mint problem can cost a batch its token but never the
 * batch itself.
 */

/**
 * Refresh this long before expiresAt so an in-flight batch never races it.
 * A token living two leads or less refreshes at half its lifetime instead.
 */
export const RYN_REPLAY_INGEST_TOKEN_REFRESH_LEAD_MS = 60_000;

/** First wait after a 401/403/404/503 mint. Doubles per consecutive refusal. */
export const RYN_REPLAY_INGEST_TOKEN_BACKOFF_BASE_MS = 30_000;

/** Longest wait between mint attempts while the server keeps refusing. */
export const RYN_REPLAY_INGEST_TOKEN_BACKOFF_CAP_MS = 15 * 60_000;

/**
 * Fixed wait after a transient failure: network error, any other non-2xx,
 * an unreadable body, or no bearer from the app. It never grows and never
 * resets the refusal backoff above; it only stops a 5 s flush cadence from
 * turning an outage into a silent IdP acquisition plus a mint per batch.
 */
export const RYN_REPLAY_INGEST_TOKEN_TRANSIENT_BACKOFF_MS = 30_000;

/** Longest the client caches a token, whatever lifetime the server reports. */
export const RYN_REPLAY_INGEST_TOKEN_TTL_MS = 10 * 60_000;

/**
 * The shortest lifetime the server can issue (RynReplay:IngestTokenTtlSeconds
 * is clamped to 60..3600). Assumed when expiresAt is unusable on this clock.
 */
export const RYN_REPLAY_INGEST_TOKEN_MIN_TTL_MS = 60_000;

/** How long the app's silent IdP acquisition may take before we use the key. */
export const RYN_REPLAY_BEARER_TIMEOUT_MS = 5_000;

/**
 * How long the mint request itself may take. The mint is single-flight, so a
 * stalled request would hold every non-unload batch of the session.
 */
export const RYN_REPLAY_MINT_TIMEOUT_MS = 10_000;

/**
 * Mint statuses that will not change from one batch to the next: unkeyed
 * server (503), bearer refused (401), origin / unmapped client / denied
 * subject (403), and a function deployed without the mint route (404). These
 * get the doubling backoff; every other failure gets the fixed transient one.
 */
const BACKOFF_STATUSES = new Set([401, 403, 404, 503]);

export interface IRynReplayIngestTokenClientOptions {
  mintUrl: string;
  bearerProvider: RynReplayBearerProvider;
  fetchRef?: typeof fetch;
  now?: () => number;
  providerTimeoutMs?: number;
  mintTimeoutMs?: number;
}

/**
 * Derive the mint url from the ingest url by replacing the trailing `/ingest`
 * path segment with `/ingest-token`. Anything else returns `null`, which
 * disables token mode: posting the app's IdP bearer to a guessed url is worse
 * than staying on the key.
 */
export function deriveRynReplayIngestTokenUrl(
  ingestUrl: string | undefined
): string | null {
  if (!ingestUrl) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(ingestUrl);
  } catch {
    return null;
  }
  const path = url.pathname.replace(/\/+$/, '');
  if (!path.endsWith('/ingest')) {
    return null;
  }
  url.pathname = `${path}-token`;
  return url.toString();
}

interface ICachedIngestToken {
  sessionId: string;
  token: string;
  refreshAtMs: number;
}

export class RynReplayIngestTokenClient implements IRynReplayIngestTokenSource {
  private cached?: ICachedIngestToken;
  private inFlight?: { sessionId: string; promise: Promise<string | null> };
  private backoffMs = 0;
  private backoffUntilMs = 0;
  /** The session the newest mint was started for; only its result is cached. */
  private latestMintSessionId?: string;

  private readonly fetchRef: typeof fetch;
  private readonly now: () => number;
  private readonly providerTimeoutMs: number;
  private readonly mintTimeoutMs: number;

  constructor(private readonly options: IRynReplayIngestTokenClientOptions) {
    this.fetchRef = options.fetchRef ?? globalThis.fetch?.bind(globalThis);
    this.now = options.now ?? Date.now;
    this.providerTimeoutMs =
      options.providerTimeoutMs ?? RYN_REPLAY_BEARER_TIMEOUT_MS;
    this.mintTimeoutMs = options.mintTimeoutMs ?? RYN_REPLAY_MINT_TIMEOUT_MS;
  }

  peekToken(sessionId: string): string | null {
    const cached = this.cached;
    return cached &&
      cached.sessionId === sessionId &&
      this.now() < cached.refreshAtMs
      ? cached.token
      : null;
  }

  getToken(sessionId: string): Promise<string | null> {
    const cached = this.peekToken(sessionId);
    if (cached) {
      return Promise.resolve(cached);
    }
    // Single-flight: a rotation flush and the unload path both send several
    // batches at once, and they should share one mint, not race N of them.
    if (this.inFlight?.sessionId === sessionId) {
      return this.inFlight.promise;
    }
    if (this.now() < this.backoffUntilMs) {
      return Promise.resolve(null);
    }

    this.latestMintSessionId = sessionId;
    const promise = this.mint(sessionId).finally(() => {
      if (this.inFlight?.promise === promise) {
        this.inFlight = undefined;
      }
    });
    this.inFlight = { sessionId, promise };
    return promise;
  }

  invalidate(sessionId: string): void {
    if (this.cached?.sessionId === sessionId) {
      this.cached = undefined;
    }
  }

  reportRejected(): void {
    this.recordBackoff();
  }

  private async mint(sessionId: string): Promise<string | null> {
    const bearer = await this.resolveBearer();
    if (!bearer) {
      this.recordTransientBackoff();
      return null;
    }

    // One timer bounds the whole exchange, body read included: the mint is
    // single-flight, so a stall anywhere in it holds every batch.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), this.mintTimeoutMs);
    try {
      return await this.requestToken(sessionId, bearer, abort.signal);
    } finally {
      clearTimeout(timer);
    }
  }

  private async requestToken(
    sessionId: string,
    bearer: string,
    signal: AbortSignal
  ): Promise<string | null> {
    let response: Response;
    try {
      response = await this.fetchRef(this.options.mintUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${bearer}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
        // The app bearer is the credential; no cookies ride along.
        credentials: 'omit',
        signal,
      });
    } catch {
      // Network error or mint timeout: no token this call, and none for a short while.
      this.recordTransientBackoff();
      return null;
    }

    if (BACKOFF_STATUSES.has(response.status)) {
      this.recordBackoff();
      return null;
    }
    if (!response.ok) {
      this.recordTransientBackoff();
      return null;
    }

    const issued = await this.readIssued(response);
    if (!issued) {
      this.recordTransientBackoff();
      return null;
    }

    this.backoffMs = 0;
    this.backoffUntilMs = 0;
    // A late mint for a session that has since rotated still serves its own
    // caller, but must not displace the newer session's cached token.
    if (this.latestMintSessionId === sessionId) {
      this.cached = { sessionId, ...issued };
    }
    return issued.token;
  }

  /**
   * The app's silent acquisition, bounded. Both pilot providers wait on an IdP
   * readiness stream that can stay pending mid-redirect, and the transport
   * awaits this before posting, so an unbounded wait would stall the flush.
   */
  private async resolveBearer(): Promise<string | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), this.providerTimeoutMs);
    });
    try {
      return await Promise.race([this.options.bearerProvider(), timeout]);
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Local refresh time from the server's expiresAt, capped at the client TTL.
   * If the client clock runs ahead the parsed expiry can look already past;
   * then assume the server's minimum lifetime, never the maximum, so a wrong
   * guess costs an early mint instead of a stale bearer. A 401 on ingest
   * still recovers either way.
   */
  private async readIssued(
    response: Response
  ): Promise<{ token: string; refreshAtMs: number } | null> {
    let body: { token?: unknown; expiresAt?: unknown };
    try {
      body = await response.json();
    } catch {
      return null;
    }
    if (typeof body?.token !== 'string' || body.token.length === 0) {
      return null;
    }

    const now = this.now();
    const expiresAtMs =
      typeof body.expiresAt === 'string' ? Date.parse(body.expiresAt) : NaN;
    const reported = expiresAtMs - now;
    const lifetimeMs =
      Number.isFinite(reported) && reported > 0
        ? Math.min(reported, RYN_REPLAY_INGEST_TOKEN_TTL_MS)
        : RYN_REPLAY_INGEST_TOKEN_MIN_TTL_MS;
    const leadMs = Math.min(
      RYN_REPLAY_INGEST_TOKEN_REFRESH_LEAD_MS,
      lifetimeMs / 2
    );

    return {
      token: body.token,
      refreshAtMs: now + lifetimeMs - leadMs,
    };
  }

  private recordBackoff(): void {
    this.backoffMs =
      this.backoffMs === 0
        ? RYN_REPLAY_INGEST_TOKEN_BACKOFF_BASE_MS
        : Math.min(this.backoffMs * 2, RYN_REPLAY_INGEST_TOKEN_BACKOFF_CAP_MS);
    this.backoffUntilMs = this.now() + this.backoffMs;
  }

  /**
   * Leaves the refusal doubling alone. getToken never mints while a wait is in
   * force, so this cannot shorten one.
   */
  private recordTransientBackoff(): void {
    this.backoffUntilMs =
      this.now() + RYN_REPLAY_INGEST_TOKEN_TRANSIENT_BACKOFF_MS;
  }
}
